import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum PhotoTypes {
  'Damage Wide-angle' = 0,
  'Damage Close-up' = 1,
  'Front Driverside' = 2,
  'Front Passenger' = 3,
  'Rear Driverside' = 4,
  'Rear Passenger' = 5,
  'Vin' = 6,
  'Odometer' = 7,
  'Lic Plate' = 8,
  'Additional Photo 1' = 9,
  'Additional Photo 2' = 10,
  'Additional Photo 3' = 11,
  'Additional Photo 4' = 12
}

export interface VehiclePhoto {
  finalUrl: string;
  type: PhotoTypes;
  uri: string;
  filename: string;
  base64: string;
  uploadStatus?: 'pending' | 'uploading' | 'completed' | 'failed';
  uploadError?: string;
}

export interface DisplayPhoto {
  /** Local preview URI; omitted after persist/rehydrate. */
  uri?: string;
  type: PhotoTypes;
  /** Final Azure Blob Storage URL (without SAS token). Populated after a successful upload. */
  url?: string;
  uploadStatus?: 'pending' | 'uploading' | 'completed' | 'failed';
  filename?: string;
}

type PartialRecord<K extends PropertyKey, T> = {
  [P in K]?: T;
};

interface VehiclePhotosState {
  photoMap: PartialRecord<PhotoTypes, DisplayPhoto>;
  uploadingCount: number;
  completedCount: number;
  failedCount: number;
}

const initialState: VehiclePhotosState = {
  photoMap: {},
  uploadingCount: 0,
  completedCount: 0,
  failedCount: 0
};

const vehiclePhotosSlice = createSlice({
  name: 'vehiclePhotos',
  initialState,
  reducers: {
    addPhoto: (state, action: PayloadAction<{ type: PhotoTypes; photo: DisplayPhoto }>) => {
      state.photoMap[action.payload.type] = action.payload.photo;
      state.completedCount = Object.keys(state.photoMap).length;
    },
    removePhoto: (state, action: PayloadAction<PhotoTypes>) => {
      const photo = state.photoMap[action.payload];
      if (photo?.uri?.startsWith('blob:')) {
        URL.revokeObjectURL(photo.uri);
      }
      delete state.photoMap[action.payload];
      state.completedCount = Object.keys(state.photoMap).length;
    },
    updatePhotoStatus: (
      state,
      action: PayloadAction<{
        type: PhotoTypes;
        status: 'pending' | 'uploading' | 'completed' | 'failed';
        error?: string;
      }>
    ) => {
      const photo = state.photoMap[action.payload.type];
      if (photo) {
        photo.uploadStatus = action.payload.status;
        
        if (action.payload.status === 'uploading') {
          state.uploadingCount++;
        } else if (action.payload.status === 'completed') {
          state.uploadingCount = Math.max(0, state.uploadingCount - 1);
          state.completedCount++;
        } else if (action.payload.status === 'failed') {
          state.uploadingCount = Math.max(0, state.uploadingCount - 1);
          state.failedCount++;
        }
      }
    },
    setPhotoMap: (state, action: PayloadAction<PartialRecord<PhotoTypes, DisplayPhoto>>) => {
      state.photoMap = action.payload;
      state.completedCount = Object.keys(state.photoMap).length;
    }
  }
});

export const {
  addPhoto,
  removePhoto,
  updatePhotoStatus,
  setPhotoMap
} = vehiclePhotosSlice.actions;

export default vehiclePhotosSlice.reducer;

// Helper functions
export const getRequiredPhotosCount = (): number => 9;

export const getRequiredPhotoTypes = (): PhotoTypes[] => [
  PhotoTypes['Damage Wide-angle'],
  PhotoTypes['Damage Close-up'],
  PhotoTypes['Front Driverside'],
  PhotoTypes['Front Passenger'],
  PhotoTypes['Rear Driverside'],
  PhotoTypes['Rear Passenger'],
  PhotoTypes['Vin'],
  PhotoTypes['Odometer'],
  PhotoTypes['Lic Plate']
];

export const getOptionalPhotoTypes = (): PhotoTypes[] => [
  PhotoTypes['Additional Photo 1'],
  PhotoTypes['Additional Photo 2'],
  PhotoTypes['Additional Photo 3'],
  PhotoTypes['Additional Photo 4']
];

export const isRequiredPhoto = (type: PhotoTypes): boolean => {
  return getRequiredPhotoTypes().includes(type);
};

/** Strip SAS/query string; empty or query-only values are not usable blob URLs. */
export const scrubBlobUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  const bare = url.split('?')[0]?.trim();
  return bare || undefined;
};

/** Ready = has a scrubbed Azure blob URL (no SAS). */
export const isPhotoUploadReady = (photo?: DisplayPhoto | null): boolean => {
  return !!scrubBlobUrl(photo?.url);
};

/** Compress/upload still in progress — not a failure. */
export const isPhotoUploadInFlight = (photo?: DisplayPhoto | null): boolean => {
  const status = photo?.uploadStatus;
  return status === 'pending' || status === 'uploading';
};

/** Present but failed / demoted — excludes in-flight uploads so UI does not flash errors. */
export const isPhotoPresentButNotReady = (photo?: DisplayPhoto | null): boolean => {
  return !!photo && !isPhotoUploadReady(photo) && !isPhotoUploadInFlight(photo);
};

export const getCompletedRequiredPhotosCount = (
  photoMap: PartialRecord<PhotoTypes, DisplayPhoto>
): number => {
  return getRequiredPhotoTypes().filter(type => isPhotoUploadReady(photoMap[type])).length;
};

export const getNotReadyRequiredPhotoTypes = (
  photoMap: PartialRecord<PhotoTypes, DisplayPhoto>
): PhotoTypes[] => {
  return getRequiredPhotoTypes().filter(type => isPhotoPresentButNotReady(photoMap[type]));
};

export const areAllRequiredPhotosReady = (
  photoMap: PartialRecord<PhotoTypes, DisplayPhoto>
): boolean => {
  return getCompletedRequiredPhotosCount(photoMap) === getRequiredPhotosCount();
};